import React, { useEffect } from 'react';
import { Building } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

export const DepositReceipt = ({
  request,
  deposit,
  receiptNumber,
  organizationInvoiceData = { orgNameInvoice: "EGARHUB", footerInvoice: "" }
}) => {
  const { t } = useTranslation();
  const currentDate = moment().format('L');
  const currentTime = moment().format('LT');

  return (
    <div className="bg-white p-8 max-w-md mx-auto font-mono text-sm">
      {/* Header */}
      <div className="text-center mb-6 border-b-2 border-dashed border-gray-300 pb-4">
        <div className="flex justify-center mb-2">
          <Building size={32} className="text-gray-600" />
        </div>
        <h1 className="text-lg font-bold text-gray-900">
          {organizationInvoiceData.orgNameInvoice || organizationInvoiceData.orgName}
        </h1>
        <p className="text-xs text-gray-500 mt-1">
          {t('depositReceipt')}
        </p>
      </div>

      {/* Receipt Info */}
      <div className="mb-4 space-y-1">
        <div className="flex justify-between">
          <span className="text-gray-600">{t('receiptNumber')}</span>
          <span className="font-semibold">{deposit?.paymentId || receiptNumber}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('date')}</span>
          <span>{currentDate}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('time')}</span>
          <span>{currentTime}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('requestNumber')}</span>
          <span>{request.reqCode}</span>
        </div>
      </div>

      <div className="border-t border-dashed border-gray-300 pt-3 mb-4"></div>

      {/* Guest Info */}
      <div className="mb-4 space-y-1">
        <h3 className="font-semibold text-gray-800 mb-2">
          {t('guestInformation')}
        </h3>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('name')}</span>
          <span className="font-medium">{request.clientName}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('phone')}</span>
          <span dir='ltr'>{request.phone}</span>
        </div>
        {/* <div className="flex justify-between">
          <span className="text-gray-600">{t('email')}</span>
          <span className="text-xs">{request.email}</span>
        </div> */}
      </div>

      <div className="border-t border-dashed border-gray-300 pt-3 mb-4"></div>

      {/* Booking Info */}
      <div className="mb-4 space-y-1">
        <h3 className="font-semibold text-gray-800 mb-2">
          {t('bookingDetails')}
        </h3>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('unit')}</span>
          <span>{request.unitName} - {request.unitCode}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('checkIn')}</span>
          <span>{moment(request.checkIn).format('L')}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('checkOut')}</span>
          <span>{moment(request.checkOut).format('L')}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('guests')}</span>
          <span>{request.guests}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('total')}</span>
          <span className="font-semibold">{request.totalAmount.toLocaleString()} {t("EGP")}</span>
        </div>
      </div>

      <div className="border-t border-dashed border-gray-300 pt-3 mb-4"></div>

      {/* Payment Info */}
      <div className="mb-6 space-y-1">
        <h3 className="font-semibold text-gray-800 mb-2">
          {t('depositDetails')}
        </h3>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('depositAmount')}</span>
          <span className="font-bold text-lg">{deposit.amountPaid.toLocaleString()} {t("EGP")}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">{t('paymentMethod')}</span>
          <span className="">{t(deposit.paymentMethod)}</span>
        </div>
        {deposit.createdAt && <div className="flex justify-between">
          <span className="text-gray-600">{t('paymentDate')}</span>
          <span>{moment(deposit.createdAt).format('L')}</span>
        </div>}
        {deposit.notes && (
          <div className="mt-2">
            <span className="text-gray-600">{t('notes')}</span>
            <p className="text-xs mt-1 text-gray-700">{deposit.notes}</p>
          </div>
        )}
      </div>

      <div className="border-t-2 border-dashed border-gray-300 pt-4 mb-6"></div>

      {/* Remaining Balance */}
      <div className="mb-6">
        <div className="flex justify-between items-center bg-gray-50 p-2 rounded">
          <span className="text-gray-700 font-medium">
            {t('remainingBalance')}
          </span>
          <span className="font-bold text-lg">
            {Math.max(0, request.totalAmount - request.deposits.reduce((sum, d) => d.paymentType === 'deposit' ? sum + d.amountPaid : sum - d.amountPaid, 0)).toLocaleString()} {t("EGP")}
          </span>
        </div>
      </div>

      {/* Signatures */}
      <div className="space-y-6 mb-6">
        <div>
          <p className="text-xs text-gray-600 mb-3">
            {t('guestSignature')}
          </p>
          <div className="border-b border-gray-400 h-8 mb-1"></div>
          <p className="text-xs text-gray-500">
            {t('guestAcknowledgment')}
          </p>
        </div>

        <div>
          <p className="text-xs text-gray-600 mb-3">
            {t('staffSignature')}
          </p>
          <div className="border-b border-gray-400 h-8 mb-1"></div>
          <p className="text-xs text-gray-500">
            {t('frontOfficeStaff')}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-xs text-gray-500 border-t border-dashed border-gray-300 pt-3">
        <p>{t('thankYou')}</p>
        <p className="mt-1" >
          {t('forInquiriesEmail')}
          <span dir='ltr'> {organizationInvoiceData.orgPhone} </span>
        </p>
        <p className="mt-2 text-xs">
          {t('keepReceipt')}
        </p>
        <p className="mt-2 text-xs">
          {organizationInvoiceData.footerInvoice}
        </p>
      </div>
    </div>
  );
};